import { BannerLite, CollectionLite, VideoLite } from '@flocasts/flosports30-types/dist/watch'
import { ContentCardAnalytics, WatchListItemAnalytics } from '../../watch/utility/watch.interfaces'
import { convertSecondsToDuration } from 'src/app/shared/functions/date-time'
import { LargeContentCardComponentProps } from './content-card.interface'
import { getSubtitleWithRelativeDate } from '../../shared/utility-functions/string.utility'

/******* TYPES *******/
export type ContentCardItem = BannerLite | CollectionLite | VideoLite
export type BannerCollectionCommonProps = Pick<
  LargeContentCardComponentProps,
  'type' | 'displayTitle' | 'destination' | 'badgeText' | 'imageThumbnailProps' | 'analyticsProps'
>

/******* FUNCTIONS *******/
/**
 * Get common props for a content card from either BannerLite or CollectionLite.
 * These types are very similar, so this function reduces duplicate code.
 */
export const getBannerOrCollectionCommonProps = (
  item: BannerLite | CollectionLite,
  commonImageProps: {
    imageSize: number | null | undefined
    mobileSize: number | null | undefined
    altText: string | null | undefined
    duration?: number | null
  },
  listItemAnalytics: WatchListItemAnalytics
): BannerCollectionCommonProps => {
  return {
    type: item.type,
    displayTitle: item.title,
    badgeText: item.label_1,
    destination: item.action.url,
    imageThumbnailProps: {
      ...commonImageProps,
      imageUrl: item.preview_image.url
    },
    /**
     * TODO: Manually typing analyticsProps as ContentCardAnalytics as long as we're on v1.2.3 of the API.
     * This is a result of the mixed types before the "future" (v2+) API endpoint is here.
     * On BannerLite and CollectionLite, the action.analytics object can, unfortunately, sometimes be null.
     * But when it is, we don't have the necessary data to build up the ContentCardAnalytics properties in another way.
     */
    analyticsProps: {
      ...listItemAnalytics,
      ...item.action.analytics
    } as ContentCardAnalytics
  }
}

/**
 * Utility function to use primary association links instead of orphan video page links, when possible.
 */
export const mapVideoToPrimaryAssociationLink = (v: VideoLite): string => {
  const nodeAssoc = v.node.primary_event_or_series_association
  const videoURI = nodeAssoc?.type === 'collection' ? 'video' : 'videos'
  return nodeAssoc ? `${nodeAssoc?.slug_uri}/${videoURI}?playing=${v.id}` : v.slug_uri
}

/**
 * Utility function to map different types of possible watch items into the LargeContentCard or FeaturedContentCard display component.
 */
export const mapDisplayItemToLargeOrFeaturedContentCard = (
  item: ContentCardItem,
  listItemAnalytics: WatchListItemAnalytics,
  userIsPremium: boolean
): LargeContentCardComponentProps => {
  const commonProps = {
    type: item.type,
    description: item.description
  }
  const commonImageProps = {
    altText: item.title,
    imageSize: 720,
    mobileSize: 720
  }

  switch (item.type) {
    case 'card':
      return {
        ...commonProps,
        ...getBannerOrCollectionCommonProps(item, commonImageProps, listItemAnalytics),
        subtext: getSubtitleWithRelativeDate(
          item.subtitle_1_parts.display_date,
          item.subtitle_1_parts.is_premium,
          userIsPremium
        )
      }
    case 'collection':
      return {
        ...commonProps,
        ...getBannerOrCollectionCommonProps(item, commonImageProps, listItemAnalytics),
        subtext: item.footer_1
      }
    case 'video':
      return {
        ...commonProps,
        displayTitle: item.short_title || item.title,
        badgeText: convertSecondsToDuration(item.duration),
        destination: mapVideoToPrimaryAssociationLink(item),
        subtext: getSubtitleWithRelativeDate(item.publish_start_date, item.premium, userIsPremium),
        imageThumbnailProps: {
          ...commonImageProps,
          imageUrl: item.asset.url
        },
        analyticsProps: {
          ...listItemAnalytics,
          node_id: item.id,
          node_type: item.type,
          primary_association_id: item.node.primary_event_or_series_association?.node_id ?? null,
          primary_association_title: item.node.primary_event_or_series_association?.title ?? null,
          is_premium: item.premium
        }
      }
  }
}
